import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Checkbox,
  Card,
  CardContent
} from '@mui/material';
import ScrollableTabs from '@components/mobile/ScrollableTabs';
import ResponsiveDataView from '@components/mobile/ResponsiveDataView';
import MobileFormDialog from '@components/mobile/MobileFormDialog';
import {
  ArrowBack,
  Edit as EditIcon,
  Cancel as CancelIcon,
  CheckCircle as CompleteIcon,
  Event as EventIcon,
  Add as AddIcon,
  CheckCircle
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { teacherAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/EmptyState';

const ManageParentMeetings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [openRescheduleDialog, setOpenRescheduleDialog] = useState(false);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rescheduleData, setRescheduleData] = useState({
    newDateTime: '',
    notes: ''
  });
  const [completeNotes, setCompleteNotes] = useState('');

  // Create meetings state - Grid-based
  const [selectedClassForCreate, setSelectedClassForCreate] = useState('');
  const [studentsGrid, setStudentsGrid] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch classes and all meetings in parallel
      const [classesResponse, meetingsResponse] = await Promise.all([
        teacherAPI.getClasses(),
        teacherAPI.getAllMeetings()
      ]);

      setClasses(classesResponse.data.classes || []);
      setMeetings(meetingsResponse.data.meetings || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Error loading data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = React.useCallback(async () => {
    try {
      setStudentsLoading(true);
      const studentsResponse = await teacherAPI.getClassStudents(selectedClassForCreate);
      const studentsList = studentsResponse.data.students || [];
      
      // Filter students with parent information and initialize grid data
      const studentsWithParents = studentsList.filter(s => s.parent_id);
      
      // Initialize grid with default values
      const gridData = studentsWithParents.map(student => ({
        id: student.id,
        student: student,
        selected: false,
        dateTime: '',
        agenda: 'Parent-Teacher Discussion',
        notes: ''
      }));
      
      setStudentsGrid(gridData);
      
      if (studentsList.length === 0) {
        setError('No students found in this class.');
      } else if (studentsWithParents.length === 0) {
        setError('No students with parent information found in this class. Please assign parents to students first.');
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setError(error.response?.data?.message || 'Error loading students. Please try again.');
    } finally {
      setStudentsLoading(false);
    }
  }, [selectedClassForCreate]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClassForCreate && activeTab === 0) {
      fetchStudents();
    }
  }, [selectedClassForCreate, activeTab, fetchStudents]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    setSuccess('');
  };

  const handleClassFilter = (event) => {
    setSelectedClass(event.target.value);
  };

  const handleClassForCreate = (event) => {
    setSelectedClassForCreate(event.target.value);
    setStudentsGrid([]);
    setError('');
  };

  const handleGridCellChange = (studentId, field, value) => {
    setStudentsGrid(prev => prev.map(row => 
      row.id === studentId ? { ...row, [field]: value } : row
    ));
  };

  const handleSelectStudent = (studentId, checked) => {
    setStudentsGrid(prev => prev.map(row => 
      row.id === studentId ? { ...row, selected: checked } : row
    ));
  };

  const handleSelectAll = (checked) => {
    setStudentsGrid(prev => prev.map(row => ({ ...row, selected: checked })));
  };

  const handleAutoSchedule = () => {
    const selectedRows = studentsGrid.filter(row => row.selected);
    if (selectedRows.length === 0) {
      setError('Please select at least one student first');
      return;
    }

    // Ask for start time
    const startTime = prompt('Enter start date and time (YYYY-MM-DDTHH:mm):');
    if (!startTime) return;

    const startDate = new Date(startTime);
    if (isNaN(startDate.getTime())) {
      setError('Invalid date format');
      return;
    }

    // Auto-schedule selected students 30 minutes apart
    let currentTime = new Date(startDate);
    setStudentsGrid(prev => prev.map(row => {
      if (row.selected) {
        const scheduledTime = new Date(currentTime);
        const formattedTime = scheduledTime.toISOString().slice(0, 16);
        currentTime = new Date(currentTime.getTime() + 30 * 60000); // Add 30 minutes
        return { ...row, dateTime: formattedTime };
      }
      return row;
    }));

    setSuccess('Auto-scheduled selected students 30 minutes apart');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSchedule = async () => {
    if (!selectedClassForCreate) {
      setError('Please select a class');
      return;
    }

    const selectedRows = studentsGrid.filter(row => row.selected && row.dateTime);
    
    if (selectedRows.length === 0) {
      setError('Please select at least one student and set a date/time');
      return;
    }

    // Validate all selected rows have date/time
    const missingDateTime = studentsGrid.some(row => row.selected && !row.dateTime);
    if (missingDateTime) {
      setError('All selected students must have a date and time set');
      return;
    }

    setSubmitLoading(true);
    setError('');
    setSuccess('');

    try {
      // Prepare meetings data for bulk create
      const meetingsData = selectedRows.map(row => ({
        student_id: row.student.id,
        parent_id: row.student.parent_id,
        scheduled_datetime: row.dateTime,
        meeting_agenda: row.agenda,
        notes: row.notes
      }));

      const response = await teacherAPI.bulkCreateMeetings({ meetings: meetingsData });
      
      setSuccess(response.data.message || `Successfully scheduled ${selectedRows.length} meeting${selectedRows.length > 1 ? 's' : ''}`);
      
      // Reset grid
      setStudentsGrid([]);
      setSelectedClassForCreate('');
      
      // Refresh meetings
      await fetchData();
      
      // Switch to view tab after 2 seconds
      setTimeout(() => {
        setActiveTab(1);
        setSuccess('');
      }, 2000);
    } catch (error) {
      console.error('Error scheduling meetings:', error);
      setError(error.response?.data?.message || 'Error scheduling meetings. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const filteredMeetings = selectedClass === 'all' 
    ? meetings 
    : meetings.filter(m => m.student?.class?.id === selectedClass);

  // Separate active and completed meetings
  const activeMeetings = filteredMeetings.filter(m => 
    m.status === 'Scheduled' || m.status === 'Rescheduled'
  );
  
  const completedMeetings = filteredMeetings.filter(m => 
    m.status === 'Completed' || m.status === 'Cancelled'
  );

  const handleOpenReschedule = (meeting) => {
    setSelectedMeeting(meeting);
    setRescheduleData({
      newDateTime: meeting.scheduled_datetime.split('.')[0],
      notes: meeting.notes || ''
    });
    setOpenRescheduleDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseReschedule = () => {
    setOpenRescheduleDialog(false);
    setSelectedMeeting(null);
    setRescheduleData({ newDateTime: '', notes: '' });
    setError('');
    setSuccess('');
  };

  const handleRescheduleChange = (e) => {
    const { name, value } = e.target;
    setRescheduleData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReschedule = async () => {
    if (!rescheduleData.newDateTime) {
      setError('Please select a new date and time');
      return;
    }

    setRescheduleLoading(true);
    setError('');
    setSuccess('');

    try {
      await teacherAPI.rescheduleMeeting(selectedMeeting.id, {
        scheduled_datetime: rescheduleData.newDateTime,
        notes: rescheduleData.notes,
        status: 'Rescheduled'
      });

      setSuccess('Meeting rescheduled successfully');
      
      // Refresh meetings
      await fetchData();
      
      setTimeout(() => {
        handleCloseReschedule();
      }, 1500);
    } catch (error) {
      console.error('Error rescheduling meeting:', error);
      setError(error.response?.data?.message || 'Error rescheduling meeting. Please try again.');
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleCancelMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to cancel this meeting?')) {
      return;
    }

    try {
      await teacherAPI.cancelMeeting(meetingId);
      setSuccess('Meeting cancelled successfully');
      await fetchData();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error cancelling meeting:', error);
      setError(error.response?.data?.message || 'Error cancelling meeting. Please try again.');
    }
  };

  const handleOpenComplete = (meeting) => {
    setSelectedMeeting(meeting);
    setCompleteNotes('');
    setOpenCompleteDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseComplete = () => {
    setOpenCompleteDialog(false);
    setSelectedMeeting(null);
    setCompleteNotes('');
    setError('');
    setSuccess('');
  };

  const handleCompleteNotesChange = (e) => {
    setCompleteNotes(e.target.value);
  };

  const handleCompleteMeeting = async () => {
    if (!completeNotes || completeNotes.trim() === '') {
      setError('Please enter meeting notes before completing');
      return;
    }

    setCompleteLoading(true);
    setError('');
    setSuccess('');

    try {
      await teacherAPI.completeMeeting(selectedMeeting.id, completeNotes);
      setSuccess('Meeting marked as completed');
      
      // Refresh meetings
      await fetchData();
      
      setTimeout(() => {
        handleCloseComplete();
      }, 1500);
    } catch (error) {
      console.error('Error completing meeting:', error);
      setError(error.response?.data?.message || 'Error completing meeting. Please try again.');
    } finally {
      setCompleteLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'primary';
      case 'Rescheduled':
        return 'warning';
      case 'Completed':
        return 'success';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Manage Parent Meetings
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/teacher/dashboard')}
        >
          Back to Dashboard
        </Button>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 0.75 }}>
        <ScrollableTabs
          value={activeTab}
          onChange={handleTabChange}
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{
            minHeight: 0,
            '& .MuiTabs-flexContainer': { gap: 1 },
            '& .MuiTab-root': {
              minHeight: 0,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2)
              },
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }
            }
          }}
        >
          <Tab label="Create Meetings" icon={<AddIcon />} iconPosition="start" />
          <Tab label="View & Manage Meetings" icon={<EventIcon />} iconPosition="start" />
        </ScrollableTabs>
      </Paper>

      {/* Create Meetings Tab */}
      {activeTab === 0 && (
        <Paper sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Schedule New Parent-Teacher Meetings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a class, then edit the meeting details directly in the table below. 
            Check the students you want to schedule meetings for, set their date/time, and save.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Select Class *</InputLabel>
              <Select
                value={selectedClassForCreate}
                onChange={handleClassForCreate}
                label="Select Class *"
              >
                <MenuItem value="">
                  <em>Select a class</em>
                </MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name}
                    {cls.grade && ` • Grade ${cls.grade}`}
                    {cls.section && ` • Section ${cls.section}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedClassForCreate && (
            <>
              {studentsLoading ? (
                <Box display="flex" justifyContent="center" py={5}>
                  <CircularProgress />
                </Box>
              ) : studentsGrid.length === 0 ? (
                <Alert severity="warning">
                  No students with parent information found in this class.
                </Alert>
              ) : (
                <>
                  <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} mb={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                      <Button
                        variant="outlined"
                        onClick={() => handleSelectAll(true)}
                      >
                        Select All
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => handleSelectAll(false)}
                      >
                        Deselect All
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<EventIcon />}
                        onClick={handleAutoSchedule}
                      >
                        Auto-Schedule
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {studentsGrid.filter(s => s.selected).length} of {studentsGrid.length} selected
                    </Typography>
                  </Box>

                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 500, overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper' }}>
                            <Checkbox
                              checked={studentsGrid.length > 0 && studentsGrid.every(s => s.selected)}
                              indeterminate={studentsGrid.some(s => s.selected) && !studentsGrid.every(s => s.selected)}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                          </TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper' }}><strong>Student</strong></TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper' }}><strong>Parent</strong></TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper', minWidth: 200 }}><strong>Date & Time *</strong></TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper', minWidth: 200 }}><strong>Meeting Agenda</strong></TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper', minWidth: 250 }}><strong>Notes</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {studentsGrid.map((row) => (
                          <TableRow 
                            key={row.id}
                            sx={{ 
                              bgcolor: row.selected ? 'action.selected' : 'inherit',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={row.selected}
                                onChange={(e) => handleSelectStudent(row.id, e.target.checked)}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {row.student.user?.name || row.student.name || 'Unknown'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {row.student.parent?.name || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="datetime-local"
                                size="small"
                                fullWidth
                                value={row.dateTime}
                                onChange={(e) => handleGridCellChange(row.id, 'dateTime', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                disabled={!row.selected}
                                required={row.selected}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                value={row.agenda}
                                onChange={(e) => handleGridCellChange(row.id, 'agenda', e.target.value)}
                                disabled={!row.selected}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                multiline
                                rows={1}
                                value={row.notes}
                                onChange={(e) => handleGridCellChange(row.id, 'notes', e.target.value)}
                                disabled={!row.selected}
                                placeholder="Optional notes..."
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Paper sx={{ p: 2, bgcolor: 'info.lighter', mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      <EventIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Tips:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 3 }}>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Select students using checkboxes, then edit their meeting details directly in the table
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Use "Auto-Schedule" to quickly set times 30 minutes apart for selected students
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Only selected students with valid date/time will be saved
                      </Typography>
                    </Box>
                  </Paper>

                  <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSelectedClassForCreate('');
                        setStudentsGrid([]);
                        setError('');
                      }}
                      disabled={submitLoading}
                    >
                      Reset
                    </Button>
                    <Button 
                      variant="contained"
                      onClick={handleSchedule}
                      disabled={submitLoading || studentsGrid.filter(r => r.selected).length === 0}
                      startIcon={submitLoading ? <CircularProgress size={20} /> : <EventIcon />}
                    >
                      {submitLoading ? 'Scheduling...' : `Schedule ${studentsGrid.filter(r => r.selected).length} Meeting${studentsGrid.filter(r => r.selected).length !== 1 ? 's' : ''}`}
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </Paper>
      )}

      {/* View & Manage Meetings Tab */}
      {activeTab === 1 && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2}>
              <Typography variant="h6">Filter Meetings</Typography>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Class</InputLabel>
                <Select
                  value={selectedClass}
                  onChange={handleClassFilter}
                  label="Filter by Class"
                >
                  <MenuItem value="all">All Classes</MenuItem>
                  {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      {cls.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {/* Active/Scheduled Meetings Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventIcon color="primary" />
              Active Meetings
              <Chip label={activeMeetings.length} size="small" color="primary" />
            </Typography>
            
            {activeMeetings.length === 0 ? (
              <EmptyState
                icon={EventIcon}
                title={selectedClass !== 'all' ? 'No active meetings found' : 'No active meetings scheduled'}
                description={selectedClass !== 'all' ? 'Try a different class filter.' : undefined}
                variant={selectedClass !== 'all' ? 'no-results' : 'empty'}
                actionLabel={selectedClass === 'all' ? 'Create Meetings' : undefined}
                onAction={selectedClass === 'all' ? () => setActiveTab(0) : undefined}
                dense
              />
            ) : (
              <ResponsiveDataView
                rows={activeMeetings}
                keyExtractor={(meeting) => meeting.id}
                renderCard={(meeting) => (
                  <Card variant="outlined" sx={{ mb: 1.5 }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                        <Typography variant="body2" fontWeight={600}>{meeting.student?.user?.name || 'N/A'}</Typography>
                        <Chip label={meeting.status} color={getStatusColor(meeting.status)} size="small" />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {meeting.student?.class?.name || 'N/A'} · {formatDateTime(meeting.scheduled_datetime)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>Parent: {meeting.parent?.name || 'N/A'}</Typography>
                      <Typography variant="body2" color="text.secondary">Agenda: {meeting.meeting_agenda || 'N/A'}</Typography>
                      <Box display="flex" gap={0.5} mt={1}>
                        <IconButton size="small" color="primary" onClick={() => handleOpenReschedule(meeting)} title="Reschedule">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="success" onClick={() => handleOpenComplete(meeting)} title="Mark as Complete">
                          <CompleteIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleCancelMeeting(meeting.id)} title="Cancel Meeting">
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                )}
                table={
                  <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Date & Time</strong></TableCell>
                          <TableCell><strong>Student</strong></TableCell>
                          <TableCell><strong>Class</strong></TableCell>
                          <TableCell><strong>Parent</strong></TableCell>
                          <TableCell><strong>Agenda</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell align="center"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activeMeetings.map((meeting) => (
                          <TableRow key={meeting.id}>
                            <TableCell>{formatDateTime(meeting.scheduled_datetime)}</TableCell>
                            <TableCell>{meeting.student?.user?.name || 'N/A'}</TableCell>
                            <TableCell>{meeting.student?.class?.name || 'N/A'}</TableCell>
                            <TableCell>{meeting.parent?.name || 'N/A'}</TableCell>
                            <TableCell>{meeting.meeting_agenda || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={meeting.status}
                                color={getStatusColor(meeting.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenReschedule(meeting)}
                                title="Reschedule"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleOpenComplete(meeting)}
                                title="Mark as Complete"
                              >
                                <CompleteIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCancelMeeting(meeting.id)}
                                title="Cancel Meeting"
                              >
                                <CancelIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                }
              />
            )}
          </Paper>

          {/* Completed Meetings Section */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle color="success" />
              Completed Meetings
              <Chip label={completedMeetings.length} size="small" color="default" />
            </Typography>
            
            {completedMeetings.length === 0 ? (
              <EmptyState
                icon={CheckCircle}
                title={selectedClass !== 'all' ? 'No completed meetings found' : 'No completed meetings yet'}
                description={selectedClass !== 'all' ? 'Try a different class filter.' : undefined}
                variant={selectedClass !== 'all' ? 'no-results' : 'empty'}
                dense
              />
            ) : (
              <ResponsiveDataView
                rows={completedMeetings}
                keyExtractor={(meeting) => meeting.id}
                renderCard={(meeting) => (
                  <Card variant="outlined" sx={{ mb: 1.5, bgcolor: 'action.hover' }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                        <Typography variant="body2" fontWeight={600}>{meeting.student?.user?.name || 'N/A'}</Typography>
                        <Chip label={meeting.status} color={getStatusColor(meeting.status)} size="small" />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {meeting.student?.class?.name || 'N/A'} · {formatDateTime(meeting.scheduled_datetime)}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>Parent: {meeting.parent?.name || 'N/A'}</Typography>
                      <Typography variant="body2" color="text.secondary">Agenda: {meeting.meeting_agenda || 'N/A'}</Typography>
                      {meeting.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Notes: {meeting.notes}</Typography>
                      )}
                    </CardContent>
                  </Card>
                )}
                table={
                  <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Date & Time</strong></TableCell>
                          <TableCell><strong>Student</strong></TableCell>
                          <TableCell><strong>Class</strong></TableCell>
                          <TableCell><strong>Parent</strong></TableCell>
                          <TableCell><strong>Agenda</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Notes</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {completedMeetings.map((meeting) => (
                          <TableRow key={meeting.id} sx={{ bgcolor: 'action.hover' }}>
                            <TableCell>{formatDateTime(meeting.scheduled_datetime)}</TableCell>
                            <TableCell>{meeting.student?.user?.name || 'N/A'}</TableCell>
                            <TableCell>{meeting.student?.class?.name || 'N/A'}</TableCell>
                            <TableCell>{meeting.parent?.name || 'N/A'}</TableCell>
                            <TableCell>{meeting.meeting_agenda || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={meeting.status}
                                color={getStatusColor(meeting.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {meeting.notes || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                }
              />
            )}
          </Paper>
        </>
      )}

      {/* Reschedule Dialog */}
      <MobileFormDialog
        open={openRescheduleDialog}
        onClose={handleCloseReschedule}
        title="Reschedule Meeting"
        actions={
          <>
            <Button onClick={handleCloseReschedule} disabled={rescheduleLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleReschedule}
              variant="contained"
              disabled={rescheduleLoading}
            >
              {rescheduleLoading ? <CircularProgress size={24} /> : 'Reschedule'}
            </Button>
          </>
        }
      >
        {selectedMeeting && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Student: <strong>{selectedMeeting.student?.user?.name}</strong>
              <br />
              Current Date: <strong>{formatDateTime(selectedMeeting.scheduled_datetime)}</strong>
            </Typography>

            {error && openRescheduleDialog && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && openRescheduleDialog && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <TextField
              label="New Date & Time"
              name="newDateTime"
              type="datetime-local"
              fullWidth
              value={rescheduleData.newDateTime}
              onChange={handleRescheduleChange}
              sx={{ mb: 2, mt: 1 }}
              InputLabelProps={{
                shrink: true,
              }}
              required
            />

            <TextField
              label="Notes (Optional)"
              name="notes"
              fullWidth
              multiline
              rows={3}
              value={rescheduleData.notes}
              onChange={handleRescheduleChange}
              placeholder="Add notes about the reschedule..."
            />
          </>
        )}
      </MobileFormDialog>

      {/* Complete Meeting Dialog */}
      <MobileFormDialog
        open={openCompleteDialog}
        onClose={handleCloseComplete}
        title="Complete Meeting"
        actions={
          <>
            <Button onClick={handleCloseComplete} disabled={completeLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleCompleteMeeting}
              variant="contained"
              color="success"
              disabled={completeLoading || !completeNotes.trim()}
            >
              {completeLoading ? <CircularProgress size={24} /> : 'Complete Meeting'}
            </Button>
          </>
        }
      >
        {selectedMeeting && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Student: <strong>{selectedMeeting.student?.user?.name}</strong>
              <br />
              Meeting Date: <strong>{formatDateTime(selectedMeeting.scheduled_datetime)}</strong>
              <br />
              Agenda: <strong>{selectedMeeting.meeting_agenda}</strong>
            </Typography>

            {error && openCompleteDialog && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && openCompleteDialog && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <TextField
              label="Meeting Notes *"
              fullWidth
              multiline
              rows={5}
              value={completeNotes}
              onChange={handleCompleteNotesChange}
              placeholder="Enter notes about what was discussed in the meeting..."
              required
              helperText="Please provide details about the meeting discussion, outcomes, and any follow-up actions."
              sx={{ mt: 1 }}
            />
          </>
        )}
      </MobileFormDialog>
    </Container>
  );
};

export default ManageParentMeetings;
